import { useCallback, useRef } from 'react';

/**
 * Custom hook for debouncing click events to prevent spam clicking
 * @param {Function} callback - The function to call when click is allowed
 * @param {number} delay - Debounce delay in milliseconds (default: 300)
 * @param {Object} options - Additional options
 * @param {boolean} options.allowSameTarget - Allow repeated clicks on same target (default: false)
 * @returns {Function} - Debounced click handler
 */
const useDebouncedClick = (callback, delay = 300, options = {}) => {
    const { allowSameTarget = false } = options;
    const lastClickTime = useRef(0);
    const lastTarget = useRef(null);
    const isProcessing = useRef(false);

    const debouncedCallback = useCallback((...args) => {
        const now = Date.now();
        const timeSinceLastClick = now - lastClickTime.current;
        
        // Extract target information for comparison (avoid circular references)
        const currentTarget = args[0]?.target || args[0]?.currentTarget || args[0];
        const targetId = currentTarget?.id ||
                        currentTarget?.dataset?.target ||
                        currentTarget?.className ||
                        (typeof args[0] === 'string' ? args[0] : 'unknown-target');

        // Prevent spam clicking
        if (isProcessing.current) {
            return;
        }

        // Check if this is a repeated click on the same target within the delay period
        if (!allowSameTarget && 
            timeSinceLastClick < delay && 
            lastTarget.current === targetId) {
            return;
        }

        // Update tracking variables
        lastClickTime.current = now;
        lastTarget.current = targetId;
        isProcessing.current = true;

        // Execute the callback
        try {
            const result = callback(...args);
            
            // If callback returns a promise, wait for it to complete
            if (result && typeof result.then === 'function') {
                result.finally(() => {
                    isProcessing.current = false;
                });
            } else {
                // For synchronous callbacks, reset after a short delay
                setTimeout(() => {
                    isProcessing.current = false;
                }, 50);
            }
        } catch (error) {
            console.error('Error in debounced callback:', error);
            isProcessing.current = false;
        }
    }, [callback, delay, allowSameTarget]);

    return debouncedCallback;
};

export default useDebouncedClick;
